/**
 * Snake Game
 * A classic arcade game where the player controls a snake that grows as it eats food.
 */

import { getHighScores, type HighScoreEntry } from '../state/highscore'

// ============================================================================
// Types
// ============================================================================

/** Callback function called when game ends */
export type GameOverCallback = (score: number) => void

interface Position {
  x: number
  y: number
}

interface GameState {
  snake: Position[]
  food: Position
  direction: Direction
  nextDirection: Direction
  score: number
  gameOver: boolean
  paused: boolean
  gridWidth: number
  gridHeight: number
  started: boolean
}

type Direction = 'up' | 'down' | 'left' | 'right'

// ============================================================================
// Constants
// ============================================================================

const CELL_SIZE = 20
const INITIAL_SNAKE_LENGTH = 3
const BASE_GAME_SPEED = 150 // ms between updates at speed level 1
const SPEED_DECREASE_PER_LEVEL = 10 // ms faster per speed level
const MIN_GAME_SPEED = 50 // fastest possible speed
const BLOCKS_PER_SPEED_LEVEL = 5 // every 5 blocks increases speed
const BASE_SCORE_PER_FOOD = 10 // base score, multiplied by speed level

/** Game version */
export const SNAKE_VERSION = '1.0.0'

// ============================================================================
// Game Logic (Exported for Testing)
// ============================================================================

/**
 * Calculate the speed level based on snake length
 * Speed increases every 5 blocks eaten
 */
export function getSpeedLevel(snakeLength: number): number {
  const blocksEaten = snakeLength - INITIAL_SNAKE_LENGTH
  return 1 + Math.floor(blocksEaten / BLOCKS_PER_SPEED_LEVEL)
}

/**
 * Calculate the game speed (ms between updates) based on speed level
 */
export function getGameSpeed(speedLevel: number): number {
  const speed = BASE_GAME_SPEED - (speedLevel - 1) * SPEED_DECREASE_PER_LEVEL
  return Math.max(speed, MIN_GAME_SPEED)
}

/**
 * Calculate the score gained for eating food based on speed level
 */
export function getScoreForFood(speedLevel: number): number {
  return BASE_SCORE_PER_FOOD * speedLevel
}

/**
 * Create the initial snake at the center of the grid
 */
export function createSnake(gridWidth: number, gridHeight: number): Position[] {
  const snake: Position[] = []
  const startX = Math.floor(gridWidth / 2)
  const startY = Math.floor(gridHeight / 2)

  for (let i = 0; i < INITIAL_SNAKE_LENGTH; i++) {
    snake.push({ x: startX - i, y: startY })
  }

  return snake
}

/**
 * Generate a random food position that doesn't overlap with the snake
 */
export function generateFood(gridWidth: number, gridHeight: number, snake: Position[]): Position {
  let food: Position
  let attempts = 0
  const maxAttempts = gridWidth * gridHeight

  do {
    food = {
      x: Math.floor(Math.random() * gridWidth),
      y: Math.floor(Math.random() * gridHeight),
    }
    attempts++
    // Prevent infinite loop if grid is full
    if (attempts > maxAttempts) {
      break
    }
  } while (isPositionOnSnake(food, snake))

  return food
}

/**
 * Check if a position overlaps with any part of the snake
 */
export function isPositionOnSnake(position: Position, snake: Position[]): boolean {
  return snake.some((segment) => segment.x === position.x && segment.y === position.y)
}

/**
 * Check if two positions are equal
 */
export function positionsEqual(a: Position, b: Position): boolean {
  return a.x === b.x && a.y === b.y
}

/**
 * Get the opposite direction (used to prevent 180-degree turns)
 */
export function getOppositeDirection(direction: Direction): Direction {
  switch (direction) {
    case 'up':
      return 'down'
    case 'down':
      return 'up'
    case 'left':
      return 'right'
    case 'right':
      return 'left'
  }
}

/**
 * Check if a direction change is valid (not a 180-degree turn)
 */
export function isValidDirectionChange(current: Direction, next: Direction): boolean {
  return next !== getOppositeDirection(current)
}

/**
 * Calculate the next head position based on direction
 */
export function getNextHeadPosition(head: Position, direction: Direction): Position {
  switch (direction) {
    case 'up':
      return { x: head.x, y: head.y - 1 }
    case 'down':
      return { x: head.x, y: head.y + 1 }
    case 'left':
      return { x: head.x - 1, y: head.y }
    case 'right':
      return { x: head.x + 1, y: head.y }
  }
}

/**
 * Check if a position is out of bounds
 */
export function isOutOfBounds(position: Position, gridWidth: number, gridHeight: number): boolean {
  return position.x < 0 || position.x >= gridWidth || position.y < 0 || position.y >= gridHeight
}

/**
 * Check if the snake collides with itself
 */
export function checkSelfCollision(snake: Position[]): boolean {
  if (snake.length < 2) return false

  const head = snake[0]
  // Check if head collides with any other segment (skip the head itself)
  return snake.slice(1).some((segment) => positionsEqual(head, segment))
}

/**
 * Move the snake in the current direction
 * Returns the new snake array and whether food was eaten
 */
export function moveSnake(
  snake: Position[],
  direction: Direction,
  food: Position,
  gridWidth: number,
  gridHeight: number
): { newSnake: Position[]; ateFood: boolean; collision: boolean } {
  const head = snake[0]
  const newHead = getNextHeadPosition(head, direction)

  // Check wall collision
  if (isOutOfBounds(newHead, gridWidth, gridHeight)) {
    return { newSnake: snake, ateFood: false, collision: true }
  }

  // Create new snake with new head
  const newSnake = [newHead, ...snake]

  // Check if we ate food
  const ateFood = positionsEqual(newHead, food)

  // If we didn't eat food, remove the tail
  if (!ateFood) {
    newSnake.pop()
  }

  // Check self collision (after adding new head)
  if (checkSelfCollision(newSnake)) {
    return { newSnake: snake, ateFood: false, collision: true }
  }

  return { newSnake, ateFood, collision: false }
}

/**
 * Create the initial game state
 */
export function createGameState(gridWidth: number, gridHeight: number): GameState {
  const snake = createSnake(gridWidth, gridHeight)
  const food = generateFood(gridWidth, gridHeight, snake)

  return {
    snake,
    food,
    direction: 'right',
    nextDirection: 'right',
    score: 0,
    gameOver: false,
    paused: false,
    gridWidth,
    gridHeight,
    started: false,
  }
}

/**
 * Update the game state for one tick
 */
export function updateGame(state: GameState): GameState {
  if (state.gameOver || state.paused) {
    return state
  }

  // Apply the next direction
  const direction = isValidDirectionChange(state.direction, state.nextDirection)
    ? state.nextDirection
    : state.direction

  // Move the snake
  const { newSnake, ateFood, collision } = moveSnake(
    state.snake,
    direction,
    state.food,
    state.gridWidth,
    state.gridHeight
  )

  if (collision) {
    return { ...state, gameOver: true }
  }

  // Generate new food if eaten
  const newFood = ateFood ? generateFood(state.gridWidth, state.gridHeight, newSnake) : state.food

  // Calculate new score based on speed level
  const speedLevel = getSpeedLevel(newSnake.length)
  const newScore = ateFood ? state.score + getScoreForFood(speedLevel) : state.score

  return {
    ...state,
    snake: newSnake,
    food: newFood,
    direction,
    nextDirection: direction,
    score: newScore,
  }
}

// ============================================================================
// Rendering
// ============================================================================

function drawGrid(
  ctx: CanvasRenderingContext2D,
  gridWidth: number,
  gridHeight: number,
  cellSize: number
): void {
  ctx.strokeStyle = 'rgba(100, 100, 100, 0.08)'
  ctx.lineWidth = 1

  for (let x = 0; x <= gridWidth; x++) {
    ctx.beginPath()
    ctx.moveTo(x * cellSize, 0)
    ctx.lineTo(x * cellSize, gridHeight * cellSize)
    ctx.stroke()
  }

  for (let y = 0; y <= gridHeight; y++) {
    ctx.beginPath()
    ctx.moveTo(0, y * cellSize)
    ctx.lineTo(gridWidth * cellSize, y * cellSize)
    ctx.stroke()
  }
}

function drawSnake(ctx: CanvasRenderingContext2D, snake: Position[], cellSize: number): void {
  snake.forEach((segment, index) => {
    const isHead = index === 0

    // Draw segment with slight padding
    const padding = 1
    ctx.fillStyle = isHead ? '#22c55e' : '#16a34a' // Green colors
    ctx.fillRect(
      segment.x * cellSize + padding,
      segment.y * cellSize + padding,
      cellSize - padding * 2,
      cellSize - padding * 2
    )

    // Draw eyes on head
    if (isHead) {
      ctx.fillStyle = '#ffffff'
      const eyeSize = 4
      const eyeOffset = 4

      ctx.beginPath()
      ctx.arc(
        segment.x * cellSize + cellSize / 2 - eyeOffset,
        segment.y * cellSize + cellSize / 2 - 2,
        eyeSize / 2,
        0,
        Math.PI * 2
      )
      ctx.fill()

      ctx.beginPath()
      ctx.arc(
        segment.x * cellSize + cellSize / 2 + eyeOffset,
        segment.y * cellSize + cellSize / 2 - 2,
        eyeSize / 2,
        0,
        Math.PI * 2
      )
      ctx.fill()
    }
  })
}

function drawFood(ctx: CanvasRenderingContext2D, food: Position, cellSize: number): void {
  ctx.fillStyle = '#ef4444' // Red

  // Draw food as a circle
  ctx.beginPath()
  ctx.arc(
    food.x * cellSize + cellSize / 2,
    food.y * cellSize + cellSize / 2,
    cellSize / 2 - 2,
    0,
    Math.PI * 2
  )
  ctx.fill()
}

function drawUI(
  ctx: CanvasRenderingContext2D,
  score: number,
  speedLevel: number,
  _canvasWidth: number
): void {
  ctx.fillStyle = '#ffffff'
  ctx.font = '16px "Press Start 2P", monospace'
  ctx.textAlign = 'left'
  ctx.fillText(`SCORE: ${score}`, 20, 30)

  ctx.fillStyle = '#22c55e' // Green for speed
  ctx.font = '12px "Press Start 2P", monospace'
  ctx.fillText(`SPEED: ${speedLevel}x`, 20, 50)
}

function drawStartScreen(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  highScores: HighScoreEntry[]
): void {
  ctx.fillStyle = 'rgba(0, 0, 0, 0.8)'
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)

  ctx.fillStyle = '#22c55e' // Green
  ctx.font = '48px "Press Start 2P", monospace'
  ctx.textAlign = 'center'
  ctx.fillText('SNAKE', canvasWidth / 2, canvasHeight / 2 - 120)

  ctx.fillStyle = '#ffffff'
  ctx.font = '14px "Press Start 2P", monospace'
  ctx.fillText('Eat food and grow longer', canvasWidth / 2, canvasHeight / 2 - 70)

  // Show high scores if available
  if (highScores.length > 0) {
    ctx.fillStyle = '#f97316' // orange
    ctx.font = '10px "Press Start 2P", monospace'
    ctx.fillText('HIGH SCORES', canvasWidth / 2, canvasHeight / 2 - 35)

    ctx.fillStyle = '#9ca3af' // gray
    ctx.font = '9px "Press Start 2P", monospace'
    const topScores = highScores.slice(0, 3)
    topScores.forEach((entry, index) => {
      const y = canvasHeight / 2 - 10 + index * 18
      ctx.fillText(
        `${index + 1}. ${entry.name}  ${entry.score.toLocaleString()}`,
        canvasWidth / 2,
        y
      )
    })
  }

  const controlsY = highScores.length > 0 ? canvasHeight / 2 + 55 : canvasHeight / 2 + 20

  ctx.fillStyle = '#ef4444' // Red
  ctx.font = '12px "Press Start 2P", monospace'
  ctx.fillText('CONTROLS', canvasWidth / 2, controlsY)

  ctx.fillStyle = '#9ca3af' // Gray
  ctx.font = '10px "Press Start 2P", monospace'
  ctx.fillText('Arrow Keys / WASD - Move', canvasWidth / 2, controlsY + 25)

  ctx.fillStyle = '#22c55e' // Green
  ctx.font = '14px "Press Start 2P", monospace'
  ctx.fillText('Press SPACE to start', canvasWidth / 2, controlsY + 70)
}

function drawGameOver(
  ctx: CanvasRenderingContext2D,
  score: number,
  canvasWidth: number,
  canvasHeight: number
): void {
  ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)

  ctx.fillStyle = '#ef4444' // Red
  ctx.font = '32px "Press Start 2P", monospace'
  ctx.textAlign = 'center'
  ctx.fillText('GAME OVER', canvasWidth / 2, canvasHeight / 2 - 40)

  ctx.fillStyle = '#ffffff'
  ctx.font = '16px "Press Start 2P", monospace'
  ctx.fillText(`FINAL SCORE: ${score}`, canvasWidth / 2, canvasHeight / 2 + 20)

  ctx.fillStyle = '#22c55e' // Green
  ctx.font = '12px "Press Start 2P", monospace'
  ctx.fillText('Press ENTER to restart', canvasWidth / 2, canvasHeight / 2 + 60)
}

// ============================================================================
// Main Game Loop
// ============================================================================

/**
 * Initialize and run the Snake game
 * @param container The HTML element to render the game in
 * @param onGameOver Optional callback called when game ends with the final score
 * @returns Cleanup function to stop the game
 */
export function loadSnake(container: HTMLElement, onGameOver?: GameOverCallback): () => void {
  // Create canvas
  const canvas = document.createElement('canvas')
  canvas.style.width = '100%'
  canvas.style.height = '100%'
  canvas.style.display = 'block'
  container.innerHTML = ''
  container.appendChild(canvas)

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    console.error('Could not get canvas context')
    return () => {}
  }

  // Store reference to avoid null checks in nested functions
  const context: CanvasRenderingContext2D = ctx

  // Track if game over callback has been called
  let gameOverCallbackFired = false

  // Calculate grid size based on container
  function calculateGridSize(): { gridWidth: number; gridHeight: number } {
    const gridWidth = Math.floor(container.clientWidth / CELL_SIZE)
    const gridHeight = Math.floor(container.clientHeight / CELL_SIZE)
    return { gridWidth, gridHeight }
  }

  // Resize canvas to match container
  function resizeCanvas(): void {
    canvas.width = container.clientWidth
    canvas.height = container.clientHeight
  }
  resizeCanvas()

  const resizeObserver = new ResizeObserver(resizeCanvas)
  resizeObserver.observe(container)

  // Initialize game state
  const { gridWidth, gridHeight } = calculateGridSize()
  let state = createGameState(gridWidth, gridHeight)

  let running = true
  let lastUpdate = 0

  // Input handler
  function handleKeyDown(e: KeyboardEvent): void {
    if (state.gameOver) {
      if (e.key === 'Enter') {
        // Restart game
        const { gridWidth, gridHeight } = calculateGridSize()
        state = createGameState(gridWidth, gridHeight)
        gameOverCallbackFired = false
      }
      return
    }

    if (!state.started) {
      if (e.key === ' ') {
        state.started = true
        e.preventDefault()
      }
      return
    }

    switch (e.key) {
      case 'ArrowUp':
      case 'w':
        state.nextDirection = 'up'
        e.preventDefault()
        break
      case 'ArrowDown':
      case 's':
        state.nextDirection = 'down'
        e.preventDefault()
        break
      case 'ArrowLeft':
      case 'a':
        state.nextDirection = 'left'
        e.preventDefault()
        break
      case 'ArrowRight':
      case 'd':
        state.nextDirection = 'right'
        e.preventDefault()
        break
    }
  }

  document.addEventListener('keydown', handleKeyDown)

  // Game loop
  function gameLoop(timestamp: number): void {
    if (!running) return

    const width = canvas.width
    const height = canvas.height

    // Clear canvas
    context.fillStyle = '#0a0a0a'
    context.fillRect(0, 0, width, height)

    // Show start screen if game hasn't started
    if (!state.started) {
      const highScores = getHighScores('snake')
      drawStartScreen(context, width, height, highScores)
      requestAnimationFrame(gameLoop)
      return
    }

    // Calculate current speed based on snake length
    const speedLevel = getSpeedLevel(state.snake.length)
    const gameSpeed = getGameSpeed(speedLevel)

    // Update game at dynamic intervals based on speed level
    if (timestamp - lastUpdate >= gameSpeed) {
      const previousGameOver = state.gameOver
      state = updateGame(state)

      // Fire game over callback once when game ends
      if (state.gameOver && !previousGameOver && !gameOverCallbackFired && onGameOver) {
        gameOverCallbackFired = true
        onGameOver(state.score)
      }

      lastUpdate = timestamp
    }

    // Draw grid
    drawGrid(context, state.gridWidth, state.gridHeight, CELL_SIZE)

    // Draw game elements
    drawFood(context, state.food, CELL_SIZE)
    drawSnake(context, state.snake, CELL_SIZE)
    drawUI(context, state.score, speedLevel, width)

    if (state.gameOver) {
      drawGameOver(context, state.score, width, height)
    }

    requestAnimationFrame(gameLoop)
  }

  // Start the game loop
  requestAnimationFrame(gameLoop)

  // Return cleanup function
  return () => {
    running = false
    document.removeEventListener('keydown', handleKeyDown)
    resizeObserver.disconnect()
    container.innerHTML = ''
  }
}
